import { useState, useCallback, useRef, type ReactNode, type MouseEvent } from 'react';

interface SplitLayoutProps {
  left: ReactNode;
  right: ReactNode;
  initialSplit?: number;
}

export function SplitLayout({ left, right, initialSplit = 50 }: SplitLayoutProps) {
  const [splitPercent, setSplitPercent] = useState(initialSplit);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);

  const handleMouseDown = useCallback((e: MouseEvent) => {
    e.preventDefault();
    isDraggingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const handleMouseMove = (moveEvent: globalThis.MouseEvent) => {
      if (!isDraggingRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = moveEvent.clientX - rect.left;
      const percent = Math.min(80, Math.max(20, (x / rect.width) * 100));
      setSplitPercent(percent);
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  return (
    <div ref={containerRef} className="flex h-full rounded-lg overflow-hidden">
      <div style={{ width: `${splitPercent}%` }} className="h-full overflow-hidden">
        {left}
      </div>
      <div
        onMouseDown={handleMouseDown}
        className="w-1 bg-transparent hover:bg-blue-400 cursor-col-resize transition-colors flex-shrink-0"
      />
      <div style={{ width: `${100 - splitPercent}%` }} className="h-full overflow-hidden bg-white">
        {right}
      </div>
    </div>
  );
}
